<template>
    <footer class="w-full">
        <!-- Testimonials on soft green wash. The radial fade is centered horizontally
             and the honeycomb pattern (faint SVG bg) gives the marketing band a subtle texture. -->
        <section
            class="relative w-full overflow-hidden"
            :style="{
                background: 'linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 35%, #F0FDF4 60%, #E2FBE8 100%)',
            }"
        >
            <!-- Honeycomb decoration: pale hexagons sitting just under the gradient. -->
            <div
                class="pointer-events-none absolute inset-0 opacity-30"
                aria-hidden="true"
                :style="{
                    backgroundImage: honeycombDataUri,
                    backgroundSize: '120px 138px',
                }"
            />
            <div class="relative mx-auto flex w-full max-w-[1440px] flex-col items-center gap-12 px-4 py-12 sm:px-8 lg:gap-[60px] lg:px-20 lg:py-[60px]">
                <div class="flex flex-col items-center gap-4">
                    <div class="flex items-center gap-2">
                        <PhStar v-for="i in 5" :key="i" :size="20" weight="fill" class="text-amber-400" />
                    </div>
                    <h3 class="text-2xl font-medium text-zinc-900">Award-Winning Privacy Protection</h3>
                </div>

                <div class="grid w-full max-w-[1280px] grid-cols-1 gap-6 md:grid-cols-3">
                    <TestimonialCard
                        quote="Privacy Bee expertly lets you take full control over your personal data online, identifying companies you trust with it and removing it from those you don't."
                        author="Neil J. Rubenking"
                        role="Lead Analyst, Security"
                    >
                        <template #logo>
                            <PcMagLogo />
                        </template>
                    </TestimonialCard>
                    <TestimonialCard
                        quote="Privacy Bee does seem like a small price to pay to prevent a massive headache in the future when the next data breach inevitably occurs."
                        author="Eileen Brown"
                        role="Contributor"
                    >
                        <template #logo>
                            <CapterraLogo />
                        </template>
                    </TestimonialCard>
                    <TestimonialCard
                        quote="Love Privacy Bee! Definitely the best option for anyone looking to cleanup their digital footprint."
                        author="User Experience"
                        role="August 2023"
                    >
                        <template #logo>
                            <TrustpilotLogo />
                        </template>
                    </TestimonialCard>
                </div>
            </div>
        </section>

        <!-- Bottom strip — matches the dark site footer from the mock. -->
        <section class="w-full bg-zinc-900 text-white">
            <div class="mx-auto w-full max-w-[1440px] px-4 py-8 sm:px-8 lg:px-20 lg:py-10">
                <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <!-- Left column: logo + copyright -->
                    <div class="flex flex-col gap-3">
                        <PbLogo aria-label="Privacy Bee" :width="143" :height="28" />
                        <p class="flex items-center gap-1.5 text-xs text-white/70">
                            <PhCopyright :size="14" weight="regular" />
                            <span>{{ year }} Privacy Bee. All Rights Reserved.</span>
                        </p>
                    </div>
                    <!-- Right column: headquarters + links -->
                    <div class="flex flex-col items-start gap-3 md:items-end">
                        <p class="text-sm text-white/90">Global Headquarters: Atlanta, GA, USA</p>
                        <div class="flex flex-wrap items-center gap-3 text-xs text-white/70">
                            <CheckoutTermsLink :business="business" class="hover:text-white">Terms &amp; Conditions</CheckoutTermsLink>
                            <span class="text-white/40" aria-hidden="true">•</span>
                            <a href="https://privacybee.com/privacy-policy/" target="_blank" rel="noopener noreferrer" class="hover:text-white">Privacy Notice</a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </footer>
</template>

<script setup lang="ts">
import { PhStar, PhCopyright } from '@phosphor-icons/vue';
import PbLogo from '@privacy-bee/design-system/vue/PbLogo.vue';
import CheckoutTermsLink from '~/components/checkout/CheckoutTermsLink.vue';

/** A business buyer's footer links the business terms; the checkout's own surfaces say so. */
defineProps<{ business?: boolean }>();

const year = new Date().getFullYear();

// Honeycomb pattern: stacked hexagons at low opacity. Inlined as a data: URI so we
// don't ship an asset; opacity is controlled on the wrapping div.
const honeycombDataUri = `url("data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 120 138'>
        <g fill='none' stroke='%2386EFAC' stroke-width='1' opacity='0.5'>
            <polygon points='30,0 60,17 60,52 30,69 0,52 0,17' />
            <polygon points='90,0 120,17 120,52 90,69 60,52 60,17' />
            <polygon points='30,69 60,86 60,121 30,138 0,121 0,86' />
            <polygon points='90,69 120,86 120,121 90,138 60,121 60,86' />
        </g>
    </svg>`,
)}")`;
</script>
